import { ChevronLeft, ChevronRight } from "lucide-react";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";

/**
 * Aperçu du bandeau d'une fiche catalogue (flèche retour, bandeau titre,
 * précédent / suivant, position) — parité avec `CatalogDetailHeader` du
 * front-public (KEEP IN SYNC). Rendu uniquement en mode `embedded` des fiches
 * d'aperçu (canvas du CMS avancé) : hors canvas, la fiche du dashboard garde sa
 * propre barre d'outils.
 *
 * Les libellés réutilisent les clés existantes du site public
 * (`catalog.backFrom*`, `catalog.previous*`, `catalog.next*`). La position
 * (« 2/24 ») est indicative : sur le site elle vient du contexte de liste.
 */
export function CatalogDetailHeaderPreview({
  title,
  resourceType,
}: {
  title: string;
  resourceType: "artwork" | "multipleprint";
}) {
  const vt = useVisibleT();
  const isMultiple = resourceType === "multipleprint";

  const backLabel = isMultiple
    ? vt("catalog.backFromMultipleprint", "Fermer la fiche")
    : vt("catalog.backFromArtwork", "Fermer la fiche");
  const previousLabel = isMultiple
    ? vt("catalog.previousMultipleprint", "Multiple précédent")
    : vt("catalog.previousArtwork", "Œuvre précédente");
  const nextLabel = isMultiple
    ? vt("catalog.nextMultipleprint", "Multiple suivant")
    : vt("catalog.nextArtwork", "Œuvre suivante");

  return (
    <header className="w-full pt-4 md:pt-6">
      <div className="relative w-full px-5 pt-2 pb-1">
        <span className="flex shrink-0 items-center gap-2 text-sm cms-page-link">
          <ChevronLeft className="size-4" aria-hidden />
          {backLabel}
        </span>
        <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold cms-page-text-muted">
          2/24
        </span>
      </div>
      <div className="relative flex min-h-[70px] w-full items-center justify-center border-y px-5 py-2 cms-page-surface cms-page-border">
        <span className="absolute left-5 top-1/2 z-10 flex -translate-y-1/2 items-center gap-1 cms-page-link">
          <ChevronLeft className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
          <span className="hidden text-sm font-semibold sm:inline">
            {previousLabel}
          </span>
        </span>
        <div className="max-w-[min(100%,calc(100%-7rem))] px-2 text-center">
          <p className="w-full truncate text-lg font-semibold">{title}</p>
        </div>
        <span className="absolute right-5 top-1/2 z-10 flex -translate-y-1/2 items-center gap-1 cms-page-link">
          <span className="hidden text-sm font-semibold sm:inline">
            {nextLabel}
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
        </span>
      </div>
    </header>
  );
}
